// Gêmeo de `apps/clients/web-chat/src/core/request_id.ts`. Duplicado de
// propósito: os dois clientes não compartilham pacote de core, e um pacote
// novo para 40 linhas custaria mais do que a cópia. Mudou aqui, mude lá.
//
// Interceptor de `fetch` que carimba um `x-request-id` por chamada.
//
// O servidor ADOTA esse id (`genReqId` no api-kit) e ele vira `request_id` em
// toda linha de log de todo serviço que atende a requisição — inclusive do
// outro lado do barramento. O id volta no header da resposta, então dá para
// lê-lo direto no DevTools.
//
// Wrapper global em vez de um `apiFetch` migrado nos arquivos que chamam
// `fetch` direto: cobertura total num lugar só. O preço é o patch de global,
// contido pela marca de idempotência abaixo.
const INSTALLED = Symbol.for('voll.requestIdInterceptor');

type MarkedFetch = typeof globalThis.fetch & { [INSTALLED]?: true };

function originOf(input: RequestInfo | URL): string | undefined {
  try {
    const raw = input instanceof Request ? input.url : input.toString();
    return new URL(raw, globalThis.location.href).origin;
  } catch {
    // URL inválida: deixa passar sem carimbo. O interceptor nunca é caminho
    // de rejeição — quem valida a URL é o próprio fetch.
    return undefined;
  }
}

/**
 * Instala o interceptor. Idempotente: chamar de novo não empilha wrapper.
 *
 * `apiOrigins` são as origens da NOSSA API. Só elas recebem o header — mandar
 * um id de correlação interno para terceiro seria vazamento.
 */
export function installRequestIdInterceptor(apiOrigins: string[]): void {
  const current = globalThis.fetch as MarkedFetch | undefined;
  if (!current || current[INSTALLED]) return;

  const allowed = new Set(
    apiOrigins.flatMap((value) => {
      try {
        return [new URL(value).origin];
      } catch {
        return [];
      }
    }),
  );

  const wrapped: MarkedFetch = async (input, init) => {
    const origin = originOf(input);
    if (origin === undefined || !allowed.has(origin)) return current(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : {}));
    if (!headers.has('x-request-id')) headers.set('x-request-id', crypto.randomUUID());
    return current(input, { ...init, headers });
  };
  wrapped[INSTALLED] = true;
  globalThis.fetch = wrapped;
}
